/* ============================================================
   Pealicious — Gift Box Option (Eco / Luxury +$20)
   Theme-agnostic build —— 适配任意主题（含自研主题）
   ------------------------------------------------------------
   放进主题 assets/pea-gift-box.js（snippet 已自带 <script defer>）

   定稿参数（2026-09-22 修订）
     · 每件商品可各配 1..5 个礼盒，每个礼盒一条独立文案 → 一单可送多人
     · 礼盒数量默认跟随本页主商品数量（客户没手动改过时）
     · 每个礼盒行带 For = 商品标题，打包时知道这个盒子配哪件货
     · 全站所有商品显示（打标签 no-gift-box 可单独关掉）

   为什么要从「每单 1 个」改成「每件可配 N 个」
     客户买 2 件产品分别送 2 个人时，只能拿到 1 个盒子 + 1 张卡，
     另一个人被漏掉。这是 2026-09-22 用户实测发现的问题。

   ⚠️ 关键设计：用 For 归属，而不是「购物车里有没有礼盒」
     旧版的判断是「车里有没有礼盒行」—— 多商品场景下会互相干扰：
     在 A 页选了礼盒，到 B 页会被认领、被改写。
     现在每个礼盒行都记着 For = 当时的商品标题：
       · 在 A 页只操作 For=A 的盒子，B 页只操作 For=B 的盒子
       · 于是「A 配 1 盒 + B 配 1 盒」能天然累加成 2 盒，互不覆盖
       · 同一商品的盒子数量则保持幂等（重复加购不会翻倍）

   为什么必须做「主题无关」
     自研主题的加购按钮实现各不相同，至少三种：
       ① <button type="submit"> + 原生表单 → 会发 submit 事件
       ② 按钮 type="button" + 自己 fetch/XHR 提交 → 不发 submit 事件
       ③ JS 里动态建表单后调用 form.submit() → 原生 submit() 不发事件
     赌错任何一种，后果不是报错，而是「礼盒静默加不进去、主商品照常
     下单」——顾客付了钱没拿到盒子，或你以为功能上线了其实没生效。

   本文件的思路：不比谁先谁后，只保证「最终一致」
     先挂 4 个探测点，只要能认出「顾客按了加购」，就把界面选择存成
     intent；然后跑一次幂等对账（reconcile），把购物车收敛到界面状态。
     对账可以重复执行、谁调用都安全 —— 所以主题加的、我们加的，
     最终结果一致。

   探测点
     path 1  原生 submit（capture 阶段拦截，最干净，不产生竞态）
     path 2  主题 fetch('/cart/add')
     path 3  主题 XMLHttpRequest → /cart/add
     path 4  JS 调 form.submit()（不发 submit 事件）→ 记 intent，下一页补
     兜底    任意页面加载时若有未完成的 intent → 补做（覆盖「加购后
             跳转到 /cart」这种页面被换掉、请求可能被中断的主题）

   对账规则（幂等，针对「本商品」的礼盒）
     界面选 Luxury · 本商品没有礼盒 → 新增 N 行礼盒（各带自己的文案）
     界面选 Luxury · 本商品已有 M 行 → 多的移除、少的补足、文案变了就更新
     界面选 Eco    · 本商品已有礼盒 → 全部移除（不动别的商品的礼盒）
     界面选 Eco    · 本商品没有礼盒 → 什么都不做
   ============================================================ */
(function () {
  'use strict';

  /* ==================== 配置 ==================== */
  var CFG = window.PeaGiftBoxConfig || {};
  var CART_PATH = CFG.cartPath || '/cart';
  var REDIRECT = CFG.redirectToCart !== false;
  var SYNC_ON_LOAD = CFG.syncWithCart !== false;
  var DEBUG = CFG.debug === true;

  var BLANK = '(blank card)';
  var ADDON_TITLE = 'Luxury Gift Box & Care Set';
  var INTENT_KEY = 'peaGiftIntent';
  var INTENT_TTL = 5 * 60 * 1000; // 兜底 intent 存活上限，防跨会话残留
  var MAX_TRIES = 3;              // 兜底补做最多重试几次，防死循环
  var DEF_MAX_BOXES = 5;

  var CURRENCY =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  var LOCALE = (document.documentElement.lang || 'en').replace('_', '-');

  var booted = false;
  var inFlight = false;
  var reconcileTimer = null;
  var selfReqs = 0; // 自己发请求时置 >0，避免被自己的钩子钩到

  function log() {
    if (!DEBUG) return;
    console.info.apply(console, ['[pea-gift-box]'].concat([].slice.call(arguments)));
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY }).format(
        cents / 100
      );
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function clampBoxes(n, max) {
    var m = max || DEF_MAX_BOXES;
    n = parseInt(n, 10);
    if (isNaN(n) || n < 1) n = 1;
    if (n > m) n = m;
    return n;
  }

  /* ==================== intent（跨页兜底意图） ==================== */
  function saveIntent(o) {
    try {
      sessionStorage.setItem(INTENT_KEY, JSON.stringify(o));
    } catch (e) {
      /* 隐私模式等场景静默失败，不影响主流程 */
    }
  }

  function readIntent() {
    try {
      var raw = sessionStorage.getItem(INTENT_KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.boxVariantId) return null;
      if (Date.now() - (o.ts || 0) > INTENT_TTL) {
        clearIntent();
        return null;
      }
      if ((o.tries || 0) >= MAX_TRIES) {
        clearIntent();
        return null;
      }
      /* 兼容第一版 intent（只有单个 message 字段） */
      if (!o.messages) o.messages = o.message ? [o.message] : [''];
      return o;
    } catch (e) {
      return null;
    }
  }

  function clearIntent() {
    try {
      sessionStorage.removeItem(INTENT_KEY);
    } catch (e) {}
  }

  function bumpTries(o) {
    o.tries = (o.tries || 0) + 1;
    if (o.tries >= MAX_TRIES) {
      clearIntent();
      console.warn('[pea-gift-box] gave up after', o.tries, 'attempts');
      return;
    }
    saveIntent(o);
    log('reconcile failed, attempt', o.tries);
  }

  /* ==================== 购物车 IO ==================== */
  function api(url, body) {
    selfReqs++;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            throw new Error(data.description || data.message || 'Cart request failed.');
          }
          return data;
        });
      })
      .then(
        function (d) {
          selfReqs--;
          return d;
        },
        function (e) {
          selfReqs--;
          throw e;
        }
      );
  }

  function getCart() {
    selfReqs++;
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return { items: [] };
      })
      .then(function (cart) {
        selfReqs--;
        return cart;
      });
  }

  /* 只取「属于这个商品」的礼盒行。
     ⚠️ 绝不能退化成「所有礼盒行」—— 那样 A 商品的盒子会在 B 商品页被认领。 */
  function findBoxLines(cart, boxVariantId, forTitle) {
    var items = (cart && cart.items) || [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (String(it.variant_id) !== String(boxVariantId)) continue;
      var f = (it.properties && it.properties['For']) || '';
      if (forTitle) {
        if (f === forTitle) out.push(it);
      } else if (!f) {
        out.push(it);
      }
    }
    return out;
  }

  function boxProps(message, forTitle) {
    var p = { 'Add-on': ADDON_TITLE, 'Personal message': message || BLANK };
    if (forTitle) p['For'] = forTitle;
    return p;
  }

  /* ==================== 对账（幂等，可重复执行） ==================== */
  function reconcile(opts) {
    var boxVariantId = String(opts.boxVariantId || '');
    if (!boxVariantId) return Promise.resolve({ skipped: 'no-variant' });

    var maxBoxes = opts.maxBoxes || DEF_MAX_BOXES;
    var luxury = !!opts.luxury;
    var forTitle = opts.forTitle || '';
    var messages = (opts.messages || []).map(function (m) {
      return String(m || '').replace(/\s+/g, ' ').trim();
    });
    var want = luxury ? clampBoxes(messages.length || 1, maxBoxes) : 0;

    return getCart().then(function (cart) {
      var mine = findBoxLines(cart, boxVariantId, forTitle);
      var chain = Promise.resolve();
      log('reconcile — own boxes in cart:', mine.length, '｜want:', want);

      /* 1) 逐行对齐：超出的移除；文案或归属不符的更新 */
      mine.forEach(function (line, idx) {
        if (idx >= want) {
          chain = chain.then(function () {
            return api('/cart/change.js', { id: line.key, quantity: 0 });
          });
          return;
        }
        var wantMsg = messages[idx] || BLANK;
        var curMsg = (line.properties && line.properties['Personal message']) || BLANK;
        var curFor = (line.properties && line.properties['For']) || '';
        if (curMsg !== wantMsg || curFor !== forTitle) {
          chain = chain.then(function () {
            return api('/cart/change.js', {
              id: line.key,
              properties: boxProps(wantMsg, forTitle)
            });
          });
        }
      });

      /* 2) 先动礼盒行、再加主商品：避免购物车出现瞬时多余的礼盒行 */
      return chain.then(function () {
        var items = [];
        if (opts.mainItem) items.push(opts.mainItem);
        for (var k = mine.length; k < want; k++) {
          items.push({
            id: Number(boxVariantId),
            quantity: 1,
            properties: boxProps(messages[k], forTitle)
          });
        }
        if (!items.length) return null;
        return api('/cart/add.js', { items: items });
      });
    });
  }

  /* ==================== 界面 ==================== */
  function findRoot() {
    return document.querySelector('[data-pea-gift]');
  }

  function readUI(root) {
    if (!root || !root.__pea) return null;
    var P = root.__pea;
    var n = P.boxCount();
    var messages = [];
    for (var i = 0; i < n; i++) messages.push(P.messageAt(i));
    return {
      luxury: P.pickedLuxury(),
      boxCount: n,
      messages: messages,
      forTitle: P.forTitle,
      maxBoxes: P.maxBoxes,
      boxVariantId: P.boxVariantId
    };
  }

  /* 主题自己处理加购时：把界面选择存成 intent，交给对账去收敛 */
  function captureIntent(root) {
    if (selfReqs > 0) return false; // 是我们自己发的请求，不重复记
    var it = readUI(root);
    if (!it || !it.boxVariantId) return false;
    saveIntent({
      luxury: it.luxury,
      messages: it.messages,
      forTitle: it.forTitle,
      boxVariantId: it.boxVariantId,
      ts: Date.now(),
      tries: 0
    });
    return true;
  }

  function refreshFromCart(root) {
    if (!root || !root.__pea) return Promise.resolve();
    var P = root.__pea;
    return getCart().then(function (cart) {
      var mine = findBoxLines(cart, P.boxVariantId, P.forTitle);
      P.state.lineKeys = mine.map(function (l) {
        return l.key;
      });
      P.paint();
    });
  }

  function showError(root, msg) {
    var el = root.querySelector('[data-pea-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  function hideError(root) {
    var el = root.querySelector('[data-pea-error]');
    if (el) el.hidden = true;
  }

  function dispatchAdded(detail) {
    document.dispatchEvent(new CustomEvent('pea:cart:added', { bubbles: true, detail: detail }));
  }

  /* ==================== 挂载一个礼盒区块 ==================== */
  /* 找页面上的加购表单（区块不在表单内时的回退） */
  function findCartForm() {
    return (
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]') ||
      null
    );
  }

  function mount(root) {
    var boxVariantId = String(root.getAttribute('data-pea-box-variant'));
    var boxCents = parseInt(root.getAttribute('data-pea-box-price-cents'), 10) || 0;
    var forTitle = root.getAttribute('data-pea-for') || '';
    var maxBoxes = parseInt(root.getAttribute('data-pea-max-boxes'), 10) || DEF_MAX_BOXES;

    /* 区块在表单里 → 用它；不在（Custom Liquid 区块/分区装法）→ 回退找表单；都没有 → null 也能跑 */
    var form = root.closest('form') || findCartForm();

    var extra = root.querySelector('[data-pea-extra]');
    var totalVal = root.querySelector('[data-pea-total-value]');
    var cartHint = root.querySelector('[data-pea-cart-hint]');
    var qtyVal = root.querySelector('[data-pea-qty-value]');
    var btnDown = root.querySelector('[data-pea-qty-down]');
    var btnUp = root.querySelector('[data-pea-qty-up]');
    var noteBoxes = [].slice.call(root.querySelectorAll('[data-pea-note-index]'));

    /* 只依赖变体 id。表单可能找不到（区块装在表单外的 Custom Liquid 区块里），
       那种情况下选项状态照样要从区块自己读、展开区照样要能开合。 */
    if (!boxVariantId) return;

    var state = {
      lineKeys: [],   // 购物车里属于本商品的礼盒行 key
      boxCount: 1,    // 界面选择的礼盒数量
      qtyTouched: false // 客户是否手动调过 stepper（调过就不再跟随主商品数量）
    };

    /* ⚠️ 必须从 root 读，绝不能从 form 读。
       Custom Liquid 区块装法下，区块在加购表单【外面】，
       form.querySelector('input[name="pea_gift_box"]') 永远返回 null
       → pickedLuxury() 恒为 false → 选 Luxury 不展开。 */
    function pickedLuxury() {
      var p = root.querySelector('input[name="pea_gift_box"]:checked');
      return !!p && p.value === 'luxury';
    }

    function noteAt(idx) {
      return noteBoxes[idx] || null;
    }

    function inputAt(idx) {
      var box = noteAt(idx);
      return box ? box.querySelector('[data-pea-note-input]') : null;
    }

    function counterAt(idx) {
      var box = noteAt(idx);
      return box ? box.querySelector('[data-pea-count]') : null;
    }

    function messageAt(idx) {
      var el = inputAt(idx);
      return el ? el.value.replace(/\s+/g, ' ').trim() : '';
    }

    function boxCount() {
      return state.boxCount;
    }

    function mainQty() {
      var el = (form || document).querySelector('[name="quantity"]');
      var n = el ? parseInt(el.value, 10) : 1;
      return isNaN(n) || n < 1 ? 1 : n;
    }

    /* 统一的数量入口：改数字、开合对应的文案框、刷新合计 */
    function setBoxCount(n, byUser) {
      if (byUser) state.qtyTouched = true;
      state.boxCount = clampBoxes(n, maxBoxes);

      if (qtyVal) qtyVal.textContent = String(state.boxCount);
      if (btnDown) btnDown.disabled = state.boxCount <= 1;
      if (btnUp) btnUp.disabled = state.boxCount >= maxBoxes;

      noteBoxes.forEach(function (box, idx) {
        var on = idx < state.boxCount;
        box.hidden = !on;
        var label = box.querySelector('[data-pea-note-label]');
        if (label) {
          label.textContent =
            state.boxCount > 1 ? 'Message for box ' + (idx + 1) : 'Personal message';
        }
      });

      paint();
    }

    /* 把车里已有的礼盒回填到界面（跳回本商品页时用） */
    function restore(messages) {
      state.qtyTouched = true; // 已有真实数据 → 不再自动跟随，避免覆盖客户的选择
      setBoxCount(Math.max(1, (messages || []).length), false);
      (messages || []).forEach(function (m, idx) {
        var el = inputAt(idx);
        if (!el) return;
        el.value = m || '';
        var c = counterAt(idx);
        if (c) c.textContent = String(el.value.length);
      });
    }

    log(
      'mounted — box variant',
      boxVariantId,
      '｜for:',
      forTitle || '(none)',
      form ? '｜form found' : '｜no form (block outside form)'
    );

    /* 商品页实时单价：主题差异大，取不到就退回 Liquid 给的变体价 */
    function baseCents() {
      var sel = CFG.priceSelector;
      if (sel) {
        var el = document.querySelector(sel);
        if (el) {
          var raw = (el.getAttribute('content') || el.textContent || '').replace(/[^\d.]/g, '');
          var n = parseFloat(raw);
          if (!isNaN(n) && n > 0) return Math.round(n * 100);
        }
      }
      return parseInt(root.getAttribute('data-pea-base-price-cents'), 10) || 0;
    }

    function paint() {
      var luxury = pickedLuxury();
      root.classList.toggle('is-luxury', luxury);
      if (extra) extra.hidden = !luxury;

      if (luxury && totalVal) {
        totalVal.textContent = money(baseCents() * mainQty() + boxCents * state.boxCount);
      }

      if (cartHint) {
        var n = state.lineKeys.length;
        var showHint = luxury && n > 0;
        cartHint.hidden = !showHint;
        if (showHint) {
          cartHint.textContent =
            n === 1
              ? 'A Luxury Gift Box for this piece is already in your order — edit the message above to update it.'
              : n +
                ' Luxury Gift Boxes for this piece are already in your order — edit the messages above to update them.';
        }
      }
    }

    /* ---------- 事件 ---------- */
    root.addEventListener('change', function (e) {
      if (e.target.name !== 'pea_gift_box') return;
      /* 首次切到 Luxury 且客户没手动改过数量 → 跟随本页主商品数量
         （买 2 件送 2 个人是常态，默认给 2 个盒子，客户可自行减到 1） */
      if (pickedLuxury() && !state.qtyTouched) {
        var q = mainQty();
        if (q > 1) setBoxCount(q, false);
        else paint();
      } else {
        paint();
      }
    });

    if (form) {
      form.addEventListener('change', function (e) {
        if (e.target.name === 'id') paint();
        if (e.target.name === 'quantity') {
          if (pickedLuxury() && !state.qtyTouched) setBoxCount(mainQty(), false);
          else paint();
        }
      });
    }

    if (btnDown) {
      btnDown.addEventListener('click', function () {
        setBoxCount(state.boxCount - 1, true);
      });
    }
    if (btnUp) {
      btnUp.addEventListener('click', function () {
        setBoxCount(state.boxCount + 1, true);
      });
    }

    noteBoxes.forEach(function (box, idx) {
      var input = box.querySelector('[data-pea-note-input]');
      var counter = box.querySelector('[data-pea-count]');
      if (!input || !counter) return;
      input.addEventListener('input', function () {
        counter.textContent = String(input.value.length);
      });
      input.addEventListener('blur', function () {
        input.value = input.value.replace(/\s+/g, ' ').trim();
        counter.textContent = String(input.value.length);
      });
    });

    root.__pea = {
      boxVariantId: boxVariantId,
      forTitle: forTitle,
      maxBoxes: maxBoxes,
      state: state,
      paint: paint,
      pickedLuxury: pickedLuxury,
      boxCount: boxCount,
      messageAt: messageAt,
      restore: restore
    };

    setBoxCount(1, false);

    /* 从别的商品页跳回来时，把购物车里的礼盒状态同步到界面。
       只认领「For = 本商品」的盒子 —— 所以在 B 商品页不会被 A 的盒子干扰。 */
    if (SYNC_ON_LOAD) {
      getCart().then(function (cart) {
        var mine = findBoxLines(cart, boxVariantId, forTitle);
        if (!mine.length) return;

        state.lineKeys = mine.map(function (l) {
          return l.key;
        });

        /* 同样必须从 root 读（见 pickedLuxury 的注释） */
        var radio = root.querySelector('input[name="pea_gift_box"][value="luxury"]');
        if (radio) radio.checked = true;

        restore(
          mine.slice(0, maxBoxes).map(function (l) {
            var msg = (l.properties && l.properties['Personal message']) || '';
            return msg === BLANK ? '' : msg;
          })
        );
      });
    }
  }

  /* ==================== 探测点 1：原生 submit ==================== */
  function isAddToCartForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') !== -1) return true;
    /* Dawn 2.0+ 及多数现代主题的标记 */
    if ((form.getAttribute('data-type') || '') === 'add-to-cart-form') return true;
    return false;
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!isAddToCartForm(form)) return;

      var root = form.querySelector('[data-pea-gift]') || findRoot();
      if (!root || !root.__pea) return;

      var ui = readUI(root);
      if (!ui) return;

      var lineKeys = root.__pea.state.lineKeys;

      /* 不选礼盒、购物车里也没有属于本商品的礼盒 → 完全不干预，交回主题 */
      if (!ui.luxury && !lineKeys.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      log('path 1: intercepted native submit — boxes:', ui.luxury ? ui.boxCount : 0);

      var fd = new FormData(form);
      var mainId = fd.get('id');
      var qty = parseInt(fd.get('quantity') || '1', 10) || 1;

      if (!mainId) {
        showError(root, 'We could not read the selected option. Please reload the page.');
        return;
      }

      /* 透传主题已收集的 line item properties（刻字、尺寸等），别吃掉 */
      var mainProps = {};
      fd.forEach(function (v, k) {
        if (typeof v === 'string' && k.indexOf('properties[') === 0) mainProps[k] = v;
      });

      var btn = form.querySelector('[type="submit"]');
      if (btn) btn.setAttribute('aria-busy', 'true');
      hideError(root);
      clearIntent(); // 我们自己会做完，不需要跨页兜底

      reconcile({
        boxVariantId: root.__pea.boxVariantId,
        forTitle: ui.forTitle,
        maxBoxes: ui.maxBoxes,
        luxury: ui.luxury,
        messages: ui.messages,
        mainItem: { id: Number(mainId), quantity: qty, properties: mainProps }
      })
        .then(function () {
          return refreshFromCart(root);
        })
        .then(function () {
          dispatchAdded({
            luxury: ui.luxury,
            boxCount: ui.luxury ? ui.boxCount : 0,
            messages: ui.messages,
            path: 'submit'
          });
          if (REDIRECT) window.location.href = CART_PATH;
        })
        .catch(function (err) {
          if (btn) btn.removeAttribute('aria-busy');
          showError(root, err.message);
          console.error('[pea-gift-box]', err);
        });
    },
    true /* capture：抢在主题自己的 handler 之前 */
  );

  /* ==================== 探测点 2 / 3 / 4 + 兜底对账 ==================== */
  function scheduleReconcile(source) {
    if (reconcileTimer || inFlight) return;
    reconcileTimer = setTimeout(function () {
      reconcileTimer = null;
      runIntentReconcile(source);
    }, 120);
  }

  function runIntentReconcile(source) {
    var it = readIntent();
    if (!it) return;

    var root = findRoot();
    var hasLine =
      root && root.__pea ? root.__pea.state.lineKeys.length > 0 : false;

    /* 选 Eco 且车里没有本商品的礼盒 → 无事可做 */
    if (!it.luxury && !hasLine) {
      clearIntent();
      return;
    }

    inFlight = true;
    log('path ' + source + ': theme handled the add — reconciling cart');

    reconcile({
      boxVariantId: it.boxVariantId,
      forTitle: it.forTitle,
      maxBoxes: (root && root.__pea && root.__pea.maxBoxes) || DEF_MAX_BOXES,
      luxury: it.luxury,
      messages: it.messages
    })
      .then(function () {
        return refreshFromCart(root);
      })
      .then(function () {
        inFlight = false;
        clearIntent();
        dispatchAdded({
          luxury: it.luxury,
          boxCount: it.luxury ? it.messages.length : 0,
          messages: it.messages,
          path: source,
          reconciled: true
        });
        log('reconciled via ' + source);
      })
      .catch(function (err) {
        inFlight = false;
        bumpTries(it);
        console.error('[pea-gift-box]', err);
      });
  }

  /* 页面加载兜底：把上一次没做完的动作补上（覆盖「加购后跳转」的主题） */
  function applyPendingIntent() {
    var it = readIntent();
    if (!it) return false;

    var root = findRoot();
    log('pending intent found — reconciling on load');
    inFlight = true;

    reconcile({
      boxVariantId: it.boxVariantId,
      forTitle: it.forTitle,
      maxBoxes: (root && root.__pea && root.__pea.maxBoxes) || DEF_MAX_BOXES,
      luxury: it.luxury,
      messages: it.messages
    })
      .then(function () {
        return refreshFromCart(root);
      })
      .then(function () {
        inFlight = false;
        clearIntent();

        /* 界面上把上次的选择恢复出来，别让顾客以为没加过 */
        if (root && root.__pea && it.luxury) {
          var radio = root.querySelector('input[name="pea_gift_box"][value="luxury"]');
          if (radio) radio.checked = true;
          /* 只在同一个商品页才回填文案 —— 否则会把 A 的文案写到 B 的框里 */
          if (root.__pea.forTitle === it.forTitle) {
            root.__pea.restore(it.messages);
          } else {
            root.__pea.paint();
          }
        }

        dispatchAdded({
          luxury: it.luxury,
          boxCount: it.luxury ? it.messages.length : 0,
          messages: it.messages,
          path: 'on-load',
          reconciled: true
        });
        log('reconciled on load');
      })
      .catch(function (err) {
        inFlight = false;
        bumpTries(it);
        console.error('[pea-gift-box]', err);
      });

    return true;
  }

  function installHooks() {
    /* --- path 2: fetch --- */
    if (typeof window.fetch === 'function' && !window.fetch.__peaHooked) {
      var nativeFetch = window.fetch;
      var patchedFetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var isAdd = String(url).indexOf('/cart/add') !== -1;
        var mine = selfReqs > 0;
        var p = nativeFetch.apply(this, arguments);
        if (isAdd && !mine) {
          var root = findRoot();
          if (root && captureIntent(root)) log('path 2: fetch add detected — intent saved');
          p.then(
            function (res) {
              if (res && res.ok) scheduleReconcile('fetch');
            },
            function () {}
          );
        }
        return p;
      };
      patchedFetch.__peaHooked = true;
      window.fetch = patchedFetch;
    }

    /* --- path 3: XMLHttpRequest --- */
    if (!XMLHttpRequest.prototype.open.__peaHooked) {
      var nativeOpen = XMLHttpRequest.prototype.open;
      var nativeSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url) {
        this.__peaUrl = url;
        return nativeOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.open.__peaHooked = true;

      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        if (String(xhr.__peaUrl || '').indexOf('/cart/add') !== -1 && selfReqs === 0) {
          var root = findRoot();
          if (root && captureIntent(root)) log('path 3: XHR add detected — intent saved');
          xhr.addEventListener('load', function () {
            if (xhr.status >= 200 && xhr.status < 300) scheduleReconcile('xhr');
          });
        }
        return nativeSend.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send.__peaHooked = true;
    }

    /* --- path 4: JS 调 form.submit()（不发 submit 事件，只能记 intent） --- */
    if (!HTMLFormElement.prototype.submit.__peaHooked) {
      var nativeFormSubmit = HTMLFormElement.prototype.submit;
      var patchedFormSubmit = function () {
        var action = this.getAttribute('action') || '';
        if (action.indexOf('/cart/add') !== -1 && selfReqs === 0) {
          var root = findRoot();
          if (root && captureIntent(root)) {
            log('path 4: programmatic form.submit() detected — intent saved for next page');
          }
        }
        return nativeFormSubmit.apply(this, arguments);
      };
      patchedFormSubmit.__peaHooked = true;
      HTMLFormElement.prototype.submit = patchedFormSubmit;
    }
  }

  /* ==================== 启动 ==================== */
  function init() {
    document.querySelectorAll('[data-pea-gift]').forEach(function (root) {
      if (!root.__pea) mount(root);
    });

    if (booted) return;
    booted = true;
    installHooks();
    if (!applyPendingIntent()) {
      log('ready (no pending intent)');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
